/**
 * Movement — the ONE player-movement simulation, shared by server and client.
 *
 * Contract:
 *   - Units are pixels and seconds. `speedPxPerSec` is pixels per second.
 *   - `stepPlayer` advances exactly one fixed tick of `dtSec` (normally 1/60).
 *   - Given identical (state, input, params, colliders) both sides produce
 *     bit-identical results: no Date.now(), no Math.random(), no globals.
 *   - Colliders are built from the same `worldObjects` array the server sends
 *     to the client on spawn, so both sides resolve against identical geometry.
 *
 * The server owns the truth: it runs stepPlayer once per received input and
 * writes the result to the player's Matter body (kinematically, via
 * setPosition). The client runs stepPlayer for prediction and replays
 * unacknowledged inputs on top of the last server state for reconciliation.
 *
 * An ES module: the server loads it with CommonJS require (Node >= 22.12), the client
 * bundle imports it.
 */

  const TICK_MS = 1000 / 60;
  const TICK_SEC = TICK_MS / 1000;
  const RESOLVE_ITERATIONS = 3;
  const EPSILON = 1e-9;

  // ───────────────────────── geometry helpers ─────────────────────────

  function rotatePoint(x, y, cx, cy, cos, sin) {
    const dx = x - cx;
    const dy = y - cy;
    return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
  }

  function rectToPolygon(cx, cy, width, height, rotationRad) {
    const hw = width / 2;
    const hh = height / 2;
    const cos = Math.cos(rotationRad);
    const sin = Math.sin(rotationRad);
    return [
      rotatePoint(cx - hw, cy - hh, cx, cy, cos, sin),
      rotatePoint(cx + hw, cy - hh, cx, cy, cos, sin),
      rotatePoint(cx + hw, cy + hh, cx, cy, cos, sin),
      rotatePoint(cx - hw, cy + hh, cx, cy, cos, sin)
    ];
  }

  function polygonFromVertices(originX, originY, relVerts, rotationRad) {
    // Vertices are relative to the object anchor — the same `vertices` array
    // PhysicsManager.createPolygonBody consumes. We keep the exact vertex ring
    // and rotate it about the vertex mean, which is the point PhysicsManager
    // passes to Bodies.fromVertices as the body position. This ring — not
    // Matter's decomposed/hulled body — is the authoritative footprint for
    // player movement, and it is identical on server and client.
    const world = relVerts.map((v) => ({ x: originX + v.x, y: originY + v.y }));
    let cx = 0;
    let cy = 0;
    for (const v of world) { cx += v.x; cy += v.y; }
    cx /= world.length;
    cy /= world.length;
    if (!rotationRad) return world;
    const cos = Math.cos(rotationRad);
    const sin = Math.sin(rotationRad);
    return world.map((v) => rotatePoint(v.x, v.y, cx, cy, cos, sin));
  }

  function makeCollider(id, verts) {
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    for (const v of verts) {
      if (v.x < minX) minX = v.x;
      if (v.x > maxX) maxX = v.x;
      if (v.y < minY) minY = v.y;
      if (v.y > maxY) maxY = v.y;
    }
    return { id, verts, minX, minY, maxX, maxY };
  }

  function pointInPolygon(px, py, verts) {
    let inside = false;
    for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
      const xi = verts[i].x; const yi = verts[i].y;
      const xj = verts[j].x; const yj = verts[j].y;
      const intersects = ((yi > py) !== (yj > py)) &&
        (px < ((xj - xi) * (py - yi)) / ((yj - yi) || EPSILON) + xi);
      if (intersects) inside = !inside;
    }
    return inside;
  }

  function closestPointOnSegment(px, py, ax, ay, bx, by) {
    const abx = bx - ax; const aby = by - ay;
    const lenSq = abx * abx + aby * aby;
    let t = lenSq > 0 ? ((px - ax) * abx + (py - ay) * aby) / lenSq : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    return { x: ax + abx * t, y: ay + aby * t };
  }

  function closestPointOnPolygon(px, py, verts) {
    let best = null;
    let bestDistSq = Infinity;
    for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
      const c = closestPointOnSegment(px, py, verts[j].x, verts[j].y, verts[i].x, verts[i].y);
      const dx = px - c.x; const dy = py - c.y;
      const d = dx * dx + dy * dy;
      if (d < bestDistSq) { bestDistSq = d; best = c; }
    }
    return { point: best, dist: Math.sqrt(bestDistSq) };
  }

  /**
   * Push a circle out of one polygon collider. Returns the corrected center,
   * or null if there was no overlap.
   */
  function resolveCircleVsPolygon(x, y, r, col) {
    if (x + r <= col.minX || x - r >= col.maxX || y + r <= col.minY || y - r >= col.maxY) return null;
    const { point: c, dist } = closestPointOnPolygon(x, y, col.verts);
    const inside = pointInPolygon(x, y, col.verts);
    if (!inside && dist >= r) return null;
    if (dist < EPSILON) {
      // Center exactly on the boundary: nudge toward the polygon's outside using
      // the direction away from the polygon's AABB center (deterministic, rare).
      const ax = x - (col.minX + col.maxX) / 2;
      const ay = y - (col.minY + col.maxY) / 2;
      const al = Math.sqrt(ax * ax + ay * ay) || 1;
      return { x: c.x + (ax / al) * r, y: c.y + (ay / al) * r };
    }
    // Outward direction: from center toward boundary when inside, away when outside.
    const nx = inside ? (c.x - x) / dist : (x - c.x) / dist;
    const ny = inside ? (c.y - y) / dist : (y - c.y) / dist;
    return { x: c.x + nx * r, y: c.y + ny * r };
  }

  // ───────────────────────── public API ─────────────────────────

  /**
   * Decide whether a world object blocks player movement. Mirrors what
   * PhysicsManager/LootContainerSystem give a solid Matter body.
   */
  function isSolid(obj) {
    if (!obj) return false;
    if (obj.isSensor) return false;
    if (typeof obj.type === 'string' && obj.type.endsWith('_pickup')) return false;
    if (obj.objectType === 'lootContainer') {
      // LootContainerSystem attaches a solid body on spawn/respawn (IDLE) and
      // detaches it on OPENING / removal, so only closed containers block.
      return obj.state === 'idle' || obj.state === 'interacting';
    }
    return true;
  }

  /**
   * Build static colliders from the world-object list both sides hold.
   * @param {Iterable<Object>|Map<string, Object>} worldObjects
   * @param {{width:number,height:number}} bounds
   * @returns {{ polygons: Array, width: number, height: number }}
   */
  function buildColliders(worldObjects, bounds) {
    const objects = worldObjects instanceof Map
      ? Array.from(worldObjects.values())
      : Array.from(worldObjects || []);
    const polygons = [];
    for (const obj of objects) {
      if (!isSolid(obj)) continue;
      const rotationRad = ((obj.rotation || 0) * Math.PI) / 180;

      if (obj.objectType === 'lootContainer') {
        // The crate you can see, not the interaction range around it.
        const body = containerBodySize(obj);
        polygons.push(makeCollider(obj.id, rectToPolygon(obj.x, obj.y, body.width, body.height, 0)));
        continue;
      }

      if (obj.isPolygon && Array.isArray(obj.vertices) && obj.vertices.length >= 3) {
        polygons.push(makeCollider(obj.id, polygonFromVertices(obj.x, obj.y, obj.vertices, rotationRad)));
        continue;
      }

      for (const coll of obj.collision || []) {
        const type = coll.type || 'wall';
        if (type === 'door' || type === 'sensor') continue; // sensors in PhysicsManager too
        polygons.push(makeCollider(
          `${obj.id}_${coll.label || 'collision'}`,
          rectToPolygon(obj.x + coll.x, obj.y + coll.y, coll.width, coll.height, rotationRad)
        ));
      }
    }
    return { polygons, width: bounds.width, height: bounds.height };
  }

  /** Resolve a circle against all colliders and the map bounds. Pure. */
  function resolveCircle(x, y, r, colliders) {
    let px = x;
    let py = y;
    for (let iter = 0; iter < RESOLVE_ITERATIONS; iter++) {
      let moved = false;
      for (const col of colliders.polygons) {
        const res = resolveCircleVsPolygon(px, py, r, col);
        if (res) { px = res.x; py = res.y; moved = true; }
      }
      if (px < r) { px = r; moved = true; }
      if (py < r) { py = r; moved = true; }
      if (px > colliders.width - r) { px = colliders.width - r; moved = true; }
      if (py > colliders.height - r) { py = colliders.height - r; moved = true; }
      if (!moved) break;
    }
    return { x: px, y: py };
  }

  /**
   * How big a loot container's physical body is, relative to its interaction box.
   *
   * A container declares ONE box in config (`interactionDimensions`) and it serves
   * two purposes: how close you must stand to open it, and — scaled by this — how
   * big the crate actually is. The crate is drawn smaller than its interaction
   * range so you can reach it comfortably without the picture filling the reach.
   *
   * This lives in shared code, not in the renderer, because it is gameplay: the
   * body you can see is the body you collide with. Deriving it separately on the
   * client is what once gave every container an invisible collision skirt.
   */
  const CONTAINER_BODY_SCALE = 0.6;

  /**
   * The physical (and drawn) box of a loot container.
   * @param {{interactionDimensions?:{width:number,height:number}, width?:number, height?:number, visual?:{scale?:number}}} obj
   * @returns {{width:number,height:number}}
   */
  function containerBodySize(obj) {
    const dims = obj.interactionDimensions || { width: obj.width || 64, height: obj.height || 64 };
    const scale = obj.visual?.scale ?? CONTAINER_BODY_SCALE;
    return { width: dims.width * scale, height: dims.height * scale };
  }

  /**
   * Projectile / tracer collision boxes for a loot container: the drawn crate
   * while it is solid, nothing once it is open. Same box Movement and the
   * renderer already share, so a bullet stops where the picture is.
   * @param {object} obj - world object (objectType 'lootContainer')
   * @returns {Array<{x:number,y:number,width:number,height:number,type:string,label:string}>}
   */
  function containerCollisionBoxes(obj) {
    if (!obj || obj.objectType !== 'lootContainer' || !isSolid(obj)) return [];
    const body = containerBodySize(obj);
    return [{ x: 0, y: 0, width: body.width, height: body.height, type: 'breakable', label: 'body' }];
  }

  /** Normalized movement direction from input booleans. */
  function inputDirection(input) {
    let dx = 0;
    let dy = 0;
    if (input.left) dx -= 1;
    if (input.right) dx += 1;
    if (input.up) dy -= 1;
    if (input.down) dy += 1;
    if (dx !== 0 && dy !== 0) {
      const inv = 1 / Math.sqrt(2);
      dx *= inv;
      dy *= inv;
    }
    return { dx, dy };
  }

  // ────────────────────── drift ──────────────────────
  //
  // Drift is the movement identity of the game: hold the drift key and you
  // slide fast in the direction you were moving, locked, until you release or
  // the meter runs dry. The meter drains while sliding and refills after a
  // short delay once you stop. Everything here is ticks and pixels — no wall
  // clock — so the client replays it exactly like the server.

  /** Default drift tuning; config/game.json player.drift overrides (see driftParams). */
  const DRIFT_DEFAULTS = Object.freeze({
    maxMeter: 100,          // meter units
    drainPerSec: 100,       // → 1 s of continuous drift from full
    regenPerSec: 12.5,      // → empty to full in 8 s (+ delay)
    regenDelayMs: 500,      // no regen for this long after you LET GO with meter left
    emptyRegenDelayMs: 3000, // …but running the meter dry is punished with a much longer wait
    minMeterToStart: 15,    // stops meter-tapping stutter drifts
    speedMultiplier: 2.2    // × normal speed while drifting (≈ 830 px/s at 378)
  });

  /**
   * Normalize drift config into the per-tick numbers stepPlayer uses.
   * @param {Object} [cfg] partial override of DRIFT_DEFAULTS (ms/seconds units)
   * @param {number} [dtSec]
   * @returns {{ maxMeter, drainPerSec, regenPerSec, regenDelayTicks, emptyRegenDelayTicks, minMeterToStart, speedMultiplier }}
   */
  function driftParams(cfg = {}, dtSec = TICK_SEC) {
    const c = { ...DRIFT_DEFAULTS, ...(cfg || {}) };
    return {
      maxMeter: c.maxMeter,
      drainPerSec: c.drainPerSec,
      regenPerSec: c.regenPerSec,
      regenDelayTicks: Math.max(0, Math.round(c.regenDelayMs / (dtSec * 1000))),
      emptyRegenDelayTicks: Math.max(0, Math.round(c.emptyRegenDelayMs / (dtSec * 1000))),
      minMeterToStart: c.minMeterToStart,
      speedMultiplier: c.speedMultiplier
    };
  }

  /** A fresh drift state (spawn): full meter, not drifting. */
  function initialDrift(params = driftParams()) {
    return { meter: params.maxMeter, active: false, dx: 0, dy: 0, held: false, regenDelay: 0 };
  }

  /**
   * Advance the drift state one tick. Pure.
   * @param {{meter:number,active:boolean,dx:number,dy:number,held:boolean,regenDelay:number}} d
   * @param {boolean} wantDrift - drift key held this tick
   * @param {{dx:number,dy:number}} move - normalized movement input direction
   * @param {number} dtSec
   * @param {ReturnType<typeof driftParams>} p
   */
  function stepDrift(d, wantDrift, move, dtSec, p) {
    let { meter, active, dx, dy, regenDelay } = d;
    const pressed = wantDrift && !d.held;      // rising edge: a new press, not a continued hold
    let sliding = false;                       // does THIS tick move at drift speed?

    if (active && !wantDrift) {
      active = false;                          // released: this tick already walks
      regenDelay = p.regenDelayTicks;
    } else if (!active && pressed && meter >= p.minMeterToStart && (move.dx !== 0 || move.dy !== 0)) {
      // You drift the way you are moving; standing still has no direction to slide in.
      active = true;
      dx = move.dx;
      dy = move.dy;
    }

    if (active) {
      sliding = true;                          // the tick that empties the meter still slides — you paid for it
      meter = Math.max(0, meter - p.drainPerSec * dtSec);
      // Running dry costs more than letting go: the long delay is the price of
      // spending the whole meter, and it is what makes pacing a slide a decision.
      if (meter <= 1e-6) { meter = 0; active = false; regenDelay = p.emptyRegenDelayTicks; }
    } else if (regenDelay > 0) {
      regenDelay -= 1;
    } else if (meter < p.maxMeter) {
      meter = Math.min(p.maxMeter, meter + p.regenPerSec * dtSec);
    }

    return { meter, active, dx, dy, held: wantDrift, regenDelay, sliding };
  }

  /**
   * Advance one player by one fixed tick.
   * @param {{x:number,y:number,drift?:Object}} state - drift is the state from initialDrift/stepDrift (omit → no drift mechanic)
   * @param {{left?:boolean,right?:boolean,up?:boolean,down?:boolean,drift?:boolean}} input
   * @param {number} dtSec - fixed tick length in seconds (TICK_SEC)
   * @param {{speedPxPerSec:number, radius:number, speedMultiplier?:number, driftBlocked?:boolean, drift?:ReturnType<typeof driftParams>}} params
   * @param {ReturnType<typeof buildColliders>} colliders
   * @returns {{x:number,y:number,vx:number,vy:number,drift?:Object}} vx/vy are the ACTUAL px/s achieved (0 when pinned against a wall)
   */
  function stepPlayer(state, input, dtSec, params, colliders) {
    const baseSpeed = params.speedPxPerSec * (params.speedMultiplier ?? 1);
    const move = inputDirection(input || {});
    let dx = move.dx;
    let dy = move.dy;
    let speed = baseSpeed;

    let drift;
    if (state.drift && params.drift) {
      // An ability that forbids sliding (invulnerability, invisibility) reads as the key being up.
      const wantDrift = Boolean(input && input.drift) && !params.driftBlocked;
      drift = stepDrift(state.drift, wantDrift, move, dtSec, params.drift);
      // Remember the real key state, so a key held through the block needs a fresh press to slide.
      drift.held = Boolean(input && input.drift);
      if (drift.sliding) {
        // Locked direction; steering input is ignored while sliding.
        dx = drift.dx;
        dy = drift.dy;
        speed = baseSpeed * params.drift.speedMultiplier;
      }
      delete drift.sliding;                    // per-tick scratch, not state
    }

    const targetX = state.x + dx * speed * dtSec;
    const targetY = state.y + dy * speed * dtSec;
    const resolved = colliders ? resolveCircle(targetX, targetY, params.radius, colliders) : { x: targetX, y: targetY };
    const out = {
      x: resolved.x,
      y: resolved.y,
      vx: (resolved.x - state.x) / dtSec,
      vy: (resolved.y - state.y) / dtSec
    };
    if (drift) {
      // Sliding into a wall you cannot get past ends the drift instead of
      // burning the meter against it (the meter already paid for this tick).
      if (drift.active && Math.abs(resolved.x - state.x) + Math.abs(resolved.y - state.y) < 1e-6) {
        drift.active = false;
        drift.regenDelay = params.drift.regenDelayTicks;
      }
      out.drift = drift;
    }
    return out;
  }

  const Movement = {
    TICK_MS,
    TICK_SEC,
    buildColliders,
    resolveCircle,
    stepPlayer,
    inputDirection,
    isSolid,
    CONTAINER_BODY_SCALE,
    containerBodySize,
    containerCollisionBoxes,
    DRIFT_DEFAULTS,
    driftParams,
    initialDrift,
    stepDrift,
    // exposed for tests
    _internals: { pointInPolygon, closestPointOnPolygon, resolveCircleVsPolygon, rectToPolygon, polygonFromVertices }
  };

// Named exports so the server's CommonJS import gets the namespace object with the
// same members the old CommonJS export had, and the client can import them.
const _internals = Movement._internals;
export { TICK_MS, TICK_SEC, buildColliders, resolveCircle, stepPlayer, inputDirection, isSolid, CONTAINER_BODY_SCALE, containerBodySize, containerCollisionBoxes, DRIFT_DEFAULTS, driftParams, initialDrift, stepDrift, _internals, Movement };
